import { compact, join, sum, values } from 'lodash';

import { Job, JobStatus } from 'interfaces/job';

export const COMPLETED_WITH_FAILURES_STATUS = 'completed_with_failures';
export const IN_PROGRESS_STATUS = 'in_progress';

export interface JobOutcomeSummary {
  displayStatus?: string;
  displayLabel?: string;
  details?: string;
}

const getCount = (counts: Record<string, number>, status: string) => counts[status] || 0;

export const getJobOutcomeSummary = (job: Partial<Job>): JobOutcomeSummary => {
  const counts = job.childStatusCounts;
  const total = sum(values(counts || {}));
  if (!counts || total === 0) {
    return { displayStatus: job.status };
  }

  const completed = getCount(counts, JobStatus.Completed);
  const failed = getCount(counts, JobStatus.Failed) + getCount(counts, JobStatus.FailedToStart);
  const running = getCount(counts, JobStatus.Running);
  const pending = getCount(counts, JobStatus.Pending);
  const cancelled = getCount(counts, JobStatus.Cancelled);
  const skipped = getCount(counts, 'skip');

  const details = join(
    compact([
      `${completed}/${total} completed`,
      failed ? `${failed}/${total} failed` : null,
      running ? `${running}/${total} running` : null,
      pending ? `${pending}/${total} pending` : null,
      cancelled ? `${cancelled}/${total} cancelled` : null,
      skipped ? `${skipped}/${total} skipped` : null,
    ]),
    ' · '
  );

  if (running + pending > 0) {
    return { displayStatus: IN_PROGRESS_STATUS, displayLabel: 'In progress', details };
  }
  if (completed === total) {
    return { displayStatus: JobStatus.Completed, displayLabel: 'Completed', details };
  }
  if (completed > 0 && failed + cancelled > 0) {
    return {
      displayStatus: COMPLETED_WITH_FAILURES_STATUS,
      displayLabel: 'Completed with failures',
      details,
    };
  }
  if (failed > 0) {
    return { displayStatus: JobStatus.Failed, displayLabel: 'Failed', details };
  }
  if (cancelled === total) {
    return { displayStatus: JobStatus.Cancelled, displayLabel: 'Cancelled', details };
  }

  return { displayStatus: job.status, details };
};
